/**
 * AppSync Events — shared WebSocket connection manager.
 *
 * Why a singleton
 * ---------------
 * AppSync Events natively multiplexes many subscriptions over a single
 * WebSocket: you open the socket once, send ``connection_init`` once, then
 * send one ``subscribe`` frame per channel (each with its own ``id``). The
 * old hook code opened a brand new WebSocket per React hook instance, so a
 * page that mounted ``useUserChannel`` + ``useAgentChannel`` +
 * ``useWorkbenchChannel`` triggered three independent 101 Switching Protocols
 * handshakes within the same paint — doubled again by ``<React.StrictMode>``
 * in dev and hammered every time Vite HMR re-ran the effects. This module
 * centralizes that into one physical socket per ``(httpDns, realtimeDns)``
 * endpoint and lets callers subscribe/unsubscribe independently.
 *
 * What this fixes versus the prior per-hook implementation
 * --------------------------------------------------------
 * 1. **1 socket for N subscriptions.** All three hooks now share one
 *    ``wss://…/event/realtime`` connection. Reconnects cost once, not N×.
 * 2. **Hard-deny stops retrying.** When AppSync replies with
 *    ``subscribe_error`` (authorizer deny — e.g. our scope-mismatch case)
 *    we mark that *specific* subscription as dead and never re-subscribe
 *    it. Previously the close that followed would schedule an exponential
 *    reconnect that looked like a 1.2s polling loop in DevTools.
 * 3. **StrictMode / HMR safe teardown.** Closing the socket the instant
 *    the last subscriber unmounts races with React 18 StrictMode's
 *    mount → unmount → mount pattern. We hold the socket open for a short
 *    grace period so the remount doesn't incur a full handshake.
 * 4. **Instance-bound listeners.** Close handlers no-op if they fire for
 *    a stale socket (the cleanup race that used to schedule a reconnect
 *    on top of the new session is impossible here).
 * 5. **Stale-socket watchdog (REALTIME-HUB.md §0.3, 2026-09-25).** A socket that dies WITHOUT a
 *    close frame (sleep, network switch, proxy idle-kill) used to freeze the room's stream forever —
 *    ``ka`` frames were ignored and nothing noticed. We now stamp every frame and force a reconnect
 *    when none arrives within AppSync's ``connectionTimeoutMs`` (+ grace), and re-check on
 *    ``online`` / tab-visible. ``connection_error`` also forces a reconnect.
 * 6. **Auth errors are not hard denies.** An expired token on reconnect surfaces as
 *    ``subscribe_error`` (Unauthorized); treating it as a permanent deny left the stream dead until a
 *    hard refresh. Auth-type errors now retry with backoff (up to ``MAX_AUTH_RETRIES``), calling an
 *    injectable token refresher (``setAppSyncAuthRefresher``) first. Only a non-auth deny is terminal.
 * 7. **No-token retry.** ``connectIfNeeded`` used to return silently with no token — never retried.
 *
 * Public API
 * ----------
 * ``subscribe(httpDns, realtimeDns, channel, cb) => unsubscribe`` — adds a
 * multiplexed subscription. ``cb.onData`` receives the raw event entries
 * AppSync delivered for this channel (strings or objects — callers decide
 * how to parse). ``cb.onStatus`` fires on connection lifecycle changes so
 * the caller can render ``connecting…`` / ``live`` indicators without
 * owning the socket.
 *
 * ``publish(httpDns, channel, payload)`` — HTTP POST the payload to the
 * Events data plane. This is the publish side of AppSync Events (WS-in,
 * HTTP-out) and is intentionally unrelated to the shared WS.
 */
import { getRestApiBearerToken } from './apiToken';

const MAX_BACKOFF_MS = 30_000;
const BASE_BACKOFF_MS = 1_200;
/** AppSync's default when ``connection_ack`` omits ``connectionTimeoutMs``. */
const DEFAULT_CONN_TIMEOUT_MS = 300_000;
/** Slack on top of the server timeout before we declare the socket dead. */
const WATCHDOG_GRACE_MS = 10_000;
const WATCHDOG_TICK_MS = 15_000;
/** On tab-visible / online: a socket silent this long is presumed dead (ka arrives ~every 60s). */
const RESUME_STALE_MS = 75_000;
/** Auth-type subscribe errors retry this many times before the entry is declared dead. */
export const MAX_AUTH_RETRIES = 5;

let authRefresher: (() => Promise<unknown> | unknown) | null = null;

/** Register a token refresher (e.g. the Cognito/session refresh) — awaited before an auth retry. */
export function setAppSyncAuthRefresher(fn: (() => Promise<unknown> | unknown) | null): void {
  authRefresher = fn;
}

/** True when an AppSync error payload is an AUTH problem (expired/invalid token), not a real deny. */
export function isAuthError(err: unknown): boolean {
  let t = '';
  try {
    t = typeof err === 'string' ? err : JSON.stringify(err ?? '');
  } catch {
    t = String(err);
  }
  return /unauthori[sz]ed|401|expired|token|forbidden.*token|jwt/i.test(t);
}

/**
 * How long to keep the shared socket open after the last subscriber
 * unmounts. Shields us from React 18 StrictMode's synchronous
 * mount → unmount → mount in dev and from Vite HMR's brief effect churn,
 * both of which would otherwise cause a full WS teardown + handshake.
 */
const IDLE_GRACE_MS = 750;

export type AppSyncEventsStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnected';

export type AppSyncEventsErrorKind = 'subscribe_error' | 'broadcast_error';

export type AppSyncEventsSubscriber = {
  /** Raw event entries from ``data`` frames for this subscription. */
  onData: (events: unknown[]) => void;
  /** Fires once per ``subscribe_success`` frame; not fired on resubscribe after reconnect if already live. */
  onSubscribed?: () => void;
  /** ``subscribe_error`` is terminal for this entry; ``broadcast_error`` is transient. */
  onError?: (err: unknown, kind: AppSyncEventsErrorKind) => void;
  /** Connection-level lifecycle for UI indicators. */
  onStatus?: (status: AppSyncEventsStatus) => void;
};

type Entry = {
  id: string;
  channel: string;
  cb: AppSyncEventsSubscriber;
  subscribed: boolean;
  /** ``true`` once AppSync has denied the subscribe — no further retries. */
  hardDenied: boolean;
  /** Auth-type subscribe errors seen so far (retried up to ``MAX_AUTH_RETRIES``). */
  authRetries: number;
};

type Manager = {
  httpDns: string;
  realtimeDns: string;
  ws: WebSocket | null;
  status: AppSyncEventsStatus;
  subs: Map<string, Entry>;
  reconnectAttempt: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  idleTimer: ReturnType<typeof setTimeout> | null;
  /** Epoch ms of the last frame of ANY type (ka/data/ack) on the current socket. */
  lastFrameAt: number;
  connTimeoutMs: number;
  watchdog: ReturnType<typeof setInterval> | null;
  lifecycleBound: boolean;
};

const managers = new Map<string, Manager>();

function keyFor(httpDns: string, realtimeDns: string): string {
  return `${httpDns}|${realtimeDns}`;
}

function wsAuthHeaderProtocol(authorization: Record<string, string>): string {
  const header = btoa(JSON.stringify(authorization))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return `header-${header}`;
}

function currentAuth(httpDns: string): Record<string, string> | null {
  const token = getRestApiBearerToken();
  if (!token) return null;
  return { host: httpDns, authorization: `Bearer ${token}` };
}

function setStatus(m: Manager, s: AppSyncEventsStatus): void {
  if (m.status === s) return;
  m.status = s;
  for (const entry of m.subs.values()) {
    try {
      entry.cb.onStatus?.(s);
    } catch {
      /* subscriber callback errors must not poison siblings */
    }
  }
}

function sendSubscribe(ws: WebSocket, entry: Entry, httpDns: string): void {
  const auth = currentAuth(httpDns);
  if (!auth) return;
  entry.subscribed = false;
  try {
    ws.send(
      JSON.stringify({
        type: 'subscribe',
        id: entry.id,
        channel: entry.channel,
        authorization: auth,
      }),
    );
  } catch {
    /* socket died mid-send — close handler will reconnect */
  }
}

/** Tear the current socket down WITHOUT waiting for a close frame, then reconnect. */
function forceReconnect(m: Manager, reason = 'stale'): void {
  const ws = m.ws;
  m.ws = null;                       // detach first → the old socket's close handler no-ops
  stopWatchdog(m);
  setStatus(m, 'disconnected');
  for (const entry of m.subs.values()) entry.subscribed = false;
  if (ws) {
    try {
      ws.close();
    } catch {
      /* already dead */
    }
  }
  // eslint-disable-next-line no-console
  console.info('[appsync] forcing reconnect:', reason);
  scheduleReconnect(m);
}

function stopWatchdog(m: Manager): void {
  if (m.watchdog) {
    clearInterval(m.watchdog);
    m.watchdog = null;
  }
}

function startWatchdog(m: Manager): void {
  stopWatchdog(m);
  m.watchdog = setInterval(() => {
    if (!m.ws) return;
    if (Date.now() - m.lastFrameAt > m.connTimeoutMs + WATCHDOG_GRACE_MS) {
      forceReconnect(m, 'no frames within connectionTimeoutMs');
    }
  }, WATCHDOG_TICK_MS);
}

/** Tab-visible / network-online: a silent socket is presumed dead; a missing one reconnects now. */
function onResume(m: Manager): void {
  if (m.subs.size === 0) return;
  if (!m.ws) {
    if (m.reconnectTimer) {
      clearTimeout(m.reconnectTimer);
      m.reconnectTimer = null;
    }
    m.reconnectAttempt = 0;
    connectIfNeeded(m);
    return;
  }
  if (Date.now() - m.lastFrameAt > RESUME_STALE_MS) forceReconnect(m, 'resume: socket silent');
}

function bindLifecycle(m: Manager): void {
  if (m.lifecycleBound || typeof window === 'undefined') return;
  m.lifecycleBound = true;
  window.addEventListener('online', () => onResume(m));
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') onResume(m);
    });
  }
}

function scheduleReconnect(m: Manager): void {
  if (m.reconnectTimer) return;
  // Reconnect only if at least one subscriber can still use the socket.
  const alive = Array.from(m.subs.values()).some((e) => !e.hardDenied);
  if (!alive) return;
  const attempt = m.reconnectAttempt;
  const delay = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * Math.pow(2, attempt));
  const jitter = Math.floor(Math.random() * 400);
  m.reconnectAttempt = attempt + 1;
  m.reconnectTimer = setTimeout(() => {
    m.reconnectTimer = null;
    connectIfNeeded(m);
  }, delay + jitter);
}

function connectIfNeeded(m: Manager): void {
  if (m.ws) return;
  if (m.subs.size === 0) return;
  const auth = currentAuth(m.httpDns);
  if (!auth) {
    // No token yet (sign-in/refresh in flight) — retry with backoff instead of giving up silently.
    setStatus(m, 'disconnected');
    scheduleReconnect(m);
    return;
  }

  const protocols = ['aws-appsync-event-ws', wsAuthHeaderProtocol(auth)];
  const ws = new WebSocket(`wss://${m.realtimeDns}/event/realtime`, protocols);
  m.ws = ws;
  m.lastFrameAt = Date.now();
  setStatus(m, 'connecting');
  bindLifecycle(m);

  // All listeners below are closures over ``ws`` and must no-op if they
  // fire for a stale socket (cleanup race, reconnect in-flight, etc.).
  ws.addEventListener('open', () => {
    if (m.ws !== ws) return;
    try {
      ws.send(JSON.stringify({ type: 'connection_init' }));
    } catch {
      /* handled by close */
    }
  });

  ws.addEventListener('message', (ev: MessageEvent) => {
    if (m.ws !== ws) return;
    let raw: unknown;
    try {
      raw = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
    } catch {
      return;
    }
    if (!raw || typeof raw !== 'object') return;
    const frame = raw as Record<string, unknown>;
    const ftype = String(frame.type || '');
    m.lastFrameAt = Date.now();          // ANY frame (incl. ka) proves the socket is alive

    if (ftype === 'connection_ack') {
      const ct = Number((frame as { connectionTimeoutMs?: unknown }).connectionTimeoutMs);
      m.connTimeoutMs = Number.isFinite(ct) && ct > 0 ? ct : DEFAULT_CONN_TIMEOUT_MS;
      startWatchdog(m);
      setStatus(m, 'connected');
      m.reconnectAttempt = 0;
      for (const entry of m.subs.values()) {
        if (entry.hardDenied) continue;
        sendSubscribe(ws, entry, m.httpDns);
      }
      return;
    }

    if (ftype === 'ka') {
      // keepalive — already stamped above; the watchdog reads lastFrameAt
      return;
    }

    if (ftype === 'connection_error') {
      // Handshake/auth failure on the connection itself → fresh socket (fresh token) with backoff.
      forceReconnect(m, 'connection_error');
      return;
    }

    if (ftype === 'subscribe_success') {
      const id = String(frame.id || '');
      const entry = m.subs.get(id);
      if (entry && !entry.subscribed) {
        entry.subscribed = true;
        entry.authRetries = 0;
        try {
          entry.cb.onSubscribed?.();
        } catch {
          /* subscriber callback errors must not poison siblings */
        }
      }
      return;
    }

    if (ftype === 'data') {
      const id = String(frame.id || '');
      const entry = m.subs.get(id);
      if (!entry) return;
      // AppSync canonical key is ``event`` (array of JSON strings); some
      // payloads use ``events`` or deliver already-parsed objects — accept
      // all shapes so we don't silently drop frames on format drift.
      const rawEv =
        (frame as { event?: unknown }).event ??
        (frame as { events?: unknown }).events;
      const list: unknown[] = Array.isArray(rawEv)
        ? rawEv
        : rawEv != null && rawEv !== ''
          ? [rawEv]
          : [];
      if (list.length) {
        try {
          entry.cb.onData(list);
        } catch {
          /* subscriber callback errors must not poison siblings */
        }
      }
      return;
    }

    if (ftype === 'subscribe_error' || ftype === 'broadcast_error') {
      const id = String(frame.id || '');
      const entry = m.subs.get(id);
      if (!entry) return;
      // ``subscribe_error`` is a hard authorizer / validation deny — retrying
      // the identical (channel, token) will just re-hit the deny, so we mark
      // this entry dead and stop resubscribing it. ``broadcast_error`` is
      // transient (publish-side failure) and leaves the subscription live.
      if (ftype === 'subscribe_error') {
        entry.subscribed = false;
        const errs = frame.errors ?? frame;
        if (isAuthError(errs) && entry.authRetries < MAX_AUTH_RETRIES) {
          // Expired/invalid token — NOT a deny. Refresh (if a refresher is registered), then
          // resubscribe with backoff. Only after MAX_AUTH_RETRIES do we give up on this entry.
          entry.authRetries += 1;
          const delay = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * Math.pow(2, entry.authRetries));
          setTimeout(async () => {
            try {
              if (authRefresher) await authRefresher();
            } catch {
              /* refresh failure → the retry below uses whatever token exists */
            }
            if (m.ws && m.status === 'connected' && m.subs.get(entry.id) === entry) {
              sendSubscribe(m.ws, entry, m.httpDns);
            }
          }, delay);
          return;
        }
        entry.hardDenied = true;
      }
      try {
        entry.cb.onError?.(
          frame.errors ?? frame,
          ftype as AppSyncEventsErrorKind,
        );
      } catch {
        /* subscriber callback errors must not poison siblings */
      }
      return;
    }
  });

  ws.addEventListener('close', () => {
    if (m.ws !== ws) return;
    m.ws = null;
    stopWatchdog(m);
    setStatus(m, 'disconnected');
    for (const entry of m.subs.values()) entry.subscribed = false;
    scheduleReconnect(m);
  });

  ws.addEventListener('error', () => {
    // The browser fires ``error`` immediately before ``close`` for a failed
    // handshake; close does the bookkeeping so there's nothing to do here.
  });
}

function scheduleIdleClose(m: Manager): void {
  if (m.idleTimer) return;
  m.idleTimer = setTimeout(() => {
    m.idleTimer = null;
    if (m.subs.size !== 0) return;
    if (m.reconnectTimer) {
      clearTimeout(m.reconnectTimer);
      m.reconnectTimer = null;
    }
    stopWatchdog(m);
    if (m.ws) {
      try {
        m.ws.close();
      } catch {
        /* socket already dead */
      }
      m.ws = null;
    }
    managers.delete(keyFor(m.httpDns, m.realtimeDns));
  }, IDLE_GRACE_MS);
}

function cancelIdleClose(m: Manager): void {
  if (m.idleTimer) {
    clearTimeout(m.idleTimer);
    m.idleTimer = null;
  }
}

function getOrCreateManager(
  httpDns: string,
  realtimeDns: string,
): Manager {
  const key = keyFor(httpDns, realtimeDns);
  let m = managers.get(key);
  if (!m) {
    m = {
      httpDns,
      realtimeDns,
      ws: null,
      status: 'idle',
      subs: new Map(),
      reconnectAttempt: 0,
      reconnectTimer: null,
      idleTimer: null,
      lastFrameAt: 0,
      connTimeoutMs: DEFAULT_CONN_TIMEOUT_MS,
      watchdog: null,
      lifecycleBound: false,
    };
    managers.set(key, m);
  }
  return m;
}

export type AppSyncEventsUnsubscribe = () => void;

/**
 * Subscribe to an AppSync Events channel over the shared WebSocket.
 * Returns an ``unsubscribe`` function; when the last subscriber for a given
 * ``(httpDns, realtimeDns)`` endpoint unsubscribes, the socket is closed
 * after a short grace period (see ``IDLE_GRACE_MS``).
 */
export function subscribeAppSyncEvents(
  httpDns: string,
  realtimeDns: string,
  channel: string,
  cb: AppSyncEventsSubscriber,
): AppSyncEventsUnsubscribe {
  if (!httpDns || !realtimeDns || !channel) {
    return () => {
      /* noop — missing config */
    };
  }

  const m = getOrCreateManager(httpDns, realtimeDns);
  cancelIdleClose(m);

  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `sub-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  const entry: Entry = {
    id,
    channel,
    cb,
    subscribed: false,
    hardDenied: false,
    authRetries: 0,
  };
  m.subs.set(id, entry);

  try {
    cb.onStatus?.(m.status === 'idle' ? 'connecting' : m.status);
  } catch {
    /* subscriber callback errors must not poison siblings */
  }

  if (!m.ws) {
    connectIfNeeded(m);
  } else if (m.status === 'connected') {
    sendSubscribe(m.ws, entry, m.httpDns);
  }

  return () => {
    const e = m.subs.get(id);
    if (!e) return;
    m.subs.delete(id);
    if (m.ws && m.status === 'connected' && e.subscribed) {
      try {
        m.ws.send(JSON.stringify({ type: 'unsubscribe', id }));
      } catch {
        /* ignore — socket will be reaped by idle close or reconnect */
      }
    }
    if (m.subs.size === 0) {
      scheduleIdleClose(m);
    }
  };
}

/**
 * HTTP publish to the AppSync Events data plane. Uses the latest bearer
 * token so callers don't need to thread it through. No connection to the
 * shared subscribe WebSocket — AppSync Events is WS-in / HTTP-out.
 */
export async function publishAppSyncEvent(
  httpDns: string,
  channel: string,
  payload: Record<string, unknown>,
): Promise<void> {
  if (!httpDns) throw new Error('Missing VITE_APPSYNC_EVENTS_HTTP_DNS');
  if (!channel) throw new Error('Missing AppSync channel');
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const body = JSON.stringify({
    channel,
    events: [JSON.stringify(payload)],
  });
  const res = await fetch(`https://${httpDns}/event`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
}

export function appsyncEventEndpoints(): {
  httpDns: string;
  realtimeDns: string;
} {
  // Vite only inlines ``VITE_*`` tokens when accessed statically — never use
  // ``import.meta.env[dynamicKey]`` or the build-time substitution is lost.
  const httpDns = String(
    import.meta.env.VITE_APPSYNC_EVENTS_HTTP_DNS ?? '',
  ).trim();
  const realtimeDns = String(
    import.meta.env.VITE_APPSYNC_EVENTS_REALTIME_DNS ?? '',
  ).trim();
  return { httpDns, realtimeDns };
}


// ── REALTIME LANES (REALTIME-HUB.md §0.6) ────────────────────────────────────────────────────────────────────────────
// The high-volume agent STREAM (``LOG_LINE`` / ``AGENT_REASONING``) publishes to its OWN Event API so it can never
// throttle the state lane. The browser learns its endpoints ONCE per session (``GET /data-plane/me/realtime``); empty
// → one-API mode (everything still arrives on the state socket). Cached; a failed lookup retries on the next call.
let _streamEndpointsPromise: Promise<{ httpDns: string; realtimeDns: string }> | null = null;

export function streamEventEndpoints(
  load: () => Promise<{ stream?: { httpDns?: string; realtimeDns?: string } }>,
): Promise<{ httpDns: string; realtimeDns: string }> {
  if (!_streamEndpointsPromise) {
    _streamEndpointsPromise = load()
      .then((r) => ({ httpDns: String(r?.stream?.httpDns || '').trim(), realtimeDns: String(r?.stream?.realtimeDns || '').trim() }))
      .catch(() => { _streamEndpointsPromise = null; return { httpDns: '', realtimeDns: '' }; });
  }
  return _streamEndpointsPromise;
}

/** Test hook. */
export function __resetStreamEndpoints() { _streamEndpointsPromise = null; }
